import {softwarePath} from "../../../shared/software-copy";
import {softwarePublicCopy} from "../../../shared/software-public-copy";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  localizedPath,
  forumPath,
  routeKeyFromSlug,
  serviceKeyFromSlug,
  type RouteKey,
} from "@/lib/routes";
import { toolIdFromSlug } from "@/features/tools/route-index";
import { api } from "@/convex/_generated/api";
import { isSupportedLocale } from "@/i18n-locales";
import { useQuery } from "convex/react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router";
import { isSecretariatSlug, SECRETARIAT_COPY } from "@/lib/secretariat";

const ROUTE_LABELS: Partial<Record<RouteKey, string>> = {
  about: "nav.about",
  careers: "nav.careers",
  services: "nav.services",
  tools: "nav.tools",
  editorial: "nav.editorial",
  sponsored: "nav.sponsored",
  portfolio: "nav.portfolio",
  partners: "nav.partners",
  blog: "nav.blog",
  shop: "nav.shop",
  contact: "nav.support",
  faq: "nav.faq",
  inquiries: "nav.inquiries",
  search: "search.label",
  privacy: "footer.privacy",
  cookie: "footer.cookie",
  terms: "footer.terms",
  withdrawal: "footer.withdrawal",
};

export default function SiteBreadcrumb() {
  const { t } = useTranslation("common");
  const { t: serviceT } = useTranslation("services");
  const { t: toolsT } = useTranslation("tools");
  const { t: magazineT } = useTranslation("magazine");
  const { t: forumT } = useTranslation("forum");
  const { lng } = useParams<{ lng: string }>();
  const location = useLocation();
  const locale = isSupportedLocale(lng) ? lng : "it";
  const segments = location.pathname.split("/").filter(Boolean);
  const routeKey = routeKeyFromSlug(segments[1] ?? "", locale);

  const articleId = Number(segments[3]);
  const isArticle =
    routeKey === "blog" && Boolean(segments[2]) && Number.isFinite(articleId);
  const article = useQuery(
    api.magazine.getArticle,
    isArticle ? { source: segments[2], externalId: articleId } : "skip",
  );
  const isShopDetail = routeKey === "shop" && Boolean(segments[2]);
  const shop = useQuery(
    api.shop.clientShops.getClientShop,
    isShopDetail ? { slug: segments[2] } : "skip",
  );
  const isForum = routeKey === "contact" && segments[2] === "forum";
  const forumPost = useQuery(
    api.crm.forum.getPublic,
    isForum && segments[3] ? { slug: segments[3] } : "skip",
  );

  if(['software','logiciels'].includes(segments[1])) return <div className="mx-auto w-full max-w-7xl px-4 py-4 sm:px-6 lg:px-8"><Breadcrumb><BreadcrumbList><BreadcrumbItem><BreadcrumbLink asChild><Link to={localizedPath('home',locale)}>{t('nav.home')}</Link></BreadcrumbLink></BreadcrumbItem><BreadcrumbSeparator/><BreadcrumbItem>{segments[2]?<BreadcrumbLink asChild><Link to={softwarePath(locale)}>{softwarePublicCopy[locale].section}</Link></BreadcrumbLink>:<BreadcrumbPage>{softwarePublicCopy[locale].section}</BreadcrumbPage>}</BreadcrumbItem>{segments[2]&&<><BreadcrumbSeparator/><BreadcrumbItem><BreadcrumbPage>{segments[2]==='enkey-pcdiag'?'Enkey PCDiag':softwarePublicCopy[locale].catalogue}</BreadcrumbPage></BreadcrumbItem></>}</BreadcrumbList></Breadcrumb></div>;
  if (!routeKey || routeKey === "home") return null;

  const routeLabel = t(ROUTE_LABELS[routeKey] ?? "nav.home");
  const crumbs: Array<{ label: string; href?: string }> = [
    { label: t("nav.home"), href: localizedPath("home", locale) },
  ];

  if (routeKey === "services" && segments[2]) {
    const serviceKey = serviceKeyFromSlug(segments[2]);
    crumbs.push({ label: routeLabel, href: localizedPath("services", locale) });
    if (serviceKey) crumbs.push({ label: serviceT(`${serviceKey}.title`) });
    else if (isSecretariatSlug(segments[2]))
      crumbs.push({ label: SECRETARIAT_COPY[locale].title });
  } else if (routeKey === "tools" && segments[2]) {
    const toolId = toolIdFromSlug(segments[2], locale);
    crumbs.push({ label: routeLabel, href: localizedPath("tools", locale) });
    if (toolId) {
      crumbs.push({ label: toolsT(`catalog.${toolId}.title`) });
    }
  } else if (routeKey === "blog" && isArticle) {
    crumbs.push({ label: routeLabel, href: localizedPath("blog", locale) });
    crumbs.push({
      label: article?.title ?? magazineT("loading"),
    });
  } else if (routeKey === "shop" && isShopDetail) {
    crumbs.push({ label: routeLabel, href: localizedPath("shop", locale) });
    crumbs.push({
      label: shop?.shop.name ?? segments[2].replaceAll("-", " "),
    });
  } else if (routeKey === "contact" && isForum) {
    crumbs.push({ label: routeLabel, href: localizedPath("contact", locale) });
    if (segments[3]) {
      crumbs.push({ label: forumT("meta.title"), href: forumPath(locale) });
      crumbs.push({
        label: forumPost?.post.subject ?? segments[3].replaceAll("-", " "),
      });
    } else {
      crumbs.push({ label: forumT("meta.title") });
    }
  } else {
    crumbs.push({ label: routeLabel });
  }

  return (
    <div className="mx-auto max-w-7xl px-4 pt-4 sm:px-6 lg:px-8">
      <Breadcrumb aria-label={t("a11y.breadcrumb")} data-site-breadcrumb>
        <BreadcrumbList>
          {crumbs.map((crumb, index) => (
            <Fragment key={`${crumb.label}-${index}`}>
              {index > 0 && <BreadcrumbSeparator />}
              <BreadcrumbItem>
                {crumb.href ? (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.href}>{crumb.label}</Link>
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                )}
              </BreadcrumbItem>
            </Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );
}
